/**
 * The item → target scoping of a brand's Knowledge lists, read from its own rows
 * (`GET /brands/:id/knowledge/scopes`) rather than from the brand row, plus the
 * reconciliation ON DISPLAY of scopes pointing at a deleted Cibles target.
 *
 * Fail closed: a failed load is `error`, never an empty scoping. The page must not send
 * `itemTargets` while the scopes are not `ready` (see `itemTargetsForSave`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TARGET_LIMITS,
  type BrandKnowledgeReconcileRequest,
  type BrandKnowledgeReconcileResponse,
  type BrandKnowledgeScopesResponse,
  type StrategyItemTargets,
} from '@traaker/shared';
import { api } from '../../../services/api';
import { idsToReconcile } from './scopedList';

export type KnowledgeScopesState =
  | { status: 'loading' }
  | { status: 'error' }
  /** `brandId`: the brand these scopes belong to (the state lags one render behind a brand switch). */
  | { status: 'ready'; brandId: string; itemTargets: StrategyItemTargets };

export interface KnowledgeScopes {
  state: KnowledgeScopesState;
  /** Refetch. Keeps the current scopes on screen while it runs; a failure keeps loaded scopes. */
  reload: () => void;
  /** Adopt a scoping the caller just saved, without waiting for a refetch. */
  setLoaded: (itemTargets: StrategyItemTargets) => void;
}

export function useKnowledgeScopes(brandId: string | undefined): KnowledgeScopes {
  const [state, setState] = useState<KnowledgeScopesState>({ status: 'loading' });
  /** Bumped by every fetch and every `setLoaded`: only the latest one may land. */
  const seq = useRef(0);

  const fetchScopes = useCallback((id: string) => {
    const mine = ++seq.current;
    api.get<BrandKnowledgeScopesResponse>(`/brands/${id}/knowledge/scopes`).then(
      (res) => { if (seq.current === mine) setState({ status: 'ready', brandId: id, itemTargets: res?.itemTargets ?? {} }); },
      () => { if (seq.current === mine) setState((s) => (s.status === 'ready' ? s : { status: 'error' })); },
    );
  }, []);

  useEffect(() => {
    setState({ status: 'loading' });
    if (brandId) fetchScopes(brandId);
    else seq.current++;
  }, [brandId, fetchScopes]);

  const reload = useCallback(() => {
    if (!brandId) return;
    setState((s) => (s.status === 'ready' ? s : { status: 'loading' }));
    fetchScopes(brandId);
  }, [brandId, fetchScopes]);

  const setLoaded = useCallback((itemTargets: StrategyItemTargets) => {
    if (!brandId) return;
    seq.current++;
    setState({ status: 'ready', brandId, itemTargets });
  }, [brandId]);

  return { state, reload, setLoaded };
}

/**
 * Once per brand display: the loaded scopes whose target is CONFIRMED deleted
 * (`missingIds`, by-id `notFound`) are unassigned server-side (`POST …/knowledge/reconcile`,
 * which re-verifies against Cibles), then the scopes are refetched so the new baseline
 * carries no phantom change. `onReassigned` receives the number of items unassigned.
 *
 * At most one attempt per brand (a ref, so StrictMode's double effect and re-renders do
 * not repeat it); a failed call is NOT retried until the next display, and the items keep
 * reading « Toutes les cibles » meanwhile. The page is never blocked.
 */
export function useReconcileDeadScopes(
  brandId: string | undefined,
  scopes: KnowledgeScopes,
  missingIds: ReadonlySet<string>,
  /** `useStrategyTargets().loading`: wait for the full `missingIds`. */
  targetsLoading: boolean,
  onReassigned: (count: number) => void,
): void {
  const attemptedFor = useRef<string | null>(null);
  const currentBrand = useRef(brandId);
  currentBrand.current = brandId;
  const latest = useRef({ reload: scopes.reload, onReassigned });
  latest.current = { reload: scopes.reload, onReassigned };

  const { state } = scopes;
  useEffect(() => {
    if (!brandId || state.status !== 'ready' || state.brandId !== brandId || targetsLoading) return;
    if (attemptedFor.current === brandId) return;
    const batches = idsToReconcile(missingIds, state.itemTargets, TARGET_LIMITS.maxTargetsByIdLookup);
    if (!batches.length) return;
    attemptedFor.current = brandId;
    Promise.allSettled(
      batches.map((targetIds) =>
        api.post<BrandKnowledgeReconcileResponse>(`/brands/${brandId}/knowledge/reconcile`, { targetIds } satisfies BrandKnowledgeReconcileRequest),
      ),
    ).then((results) => {
      if (currentBrand.current !== brandId) return;
      const unassigned = results.reduce((n, r) => n + (r.status === 'fulfilled' ? r.value?.unassigned ?? 0 : 0), 0);
      if (unassigned > 0) {
        latest.current.onReassigned(unassigned);
        latest.current.reload();
      }
    });
  }, [brandId, state, missingIds, targetsLoading]);
}
